import { useState } from "preact/hooks";
import {
  script,
  replaceScript,
  selection,
  scope,
  busy,
  selectWord,
  changeScope,
  annotate,
  editorDraft,
  editing,
  duration,
  storageNotice,
  cards,
  changePromptMode,
} from "./state";
import { parseWords, parseCards, formatTime, type PromptMode } from "./script";
import { ScriptTransfer } from "./script-transfer";
import { setSelectionPause } from "./state";
import { Icon } from "./icons";

export function ScriptEditor({ active }: { active: boolean }) {
  const [importOpen, setImportOpen] = useState(false);
  const isCards = script.value.mode === "cue-cards";
  const selected = selection.value;
  const selectedWords = selected
    ? script.value.words.slice(selected[0], selected[1] + 1)
    : [];
  const paces = new Set(
    selectedWords.map(function (word) {
      return word.pace;
    }),
  );
  const intensities = new Set(
    selectedWords.map(function (word) {
      return word.intensity;
    }),
  );
  const pace = selectedWords[0]?.pace || 140;
  let selectionLabel = "Nothing selected";
  if (selected) {
    const wordLabel = selectedWords.length === 1 ? "word" : "words";
    selectionLabel = `${selectedWords.length} ${wordLabel} selected`;
  }
  const selectionHint =
    scope.value === "range"
      ? "Tap the first word, then the last word of a phrase."
      : "Tap a word to give your reading a little direction.";
  return (
    <section
      class={`editor panel ${active ? "mobile-active" : ""}`}
      aria-label="Script editor"
    >
      <div class="panel-heading">
        <label class="title-label">
          <input
            class="script-title"
            aria-label="Script title"
            value={script.value.title}
            disabled={busy.value}
            maxLength={100}
            onInput={function (event) {
              script.value = {
                ...script.value,
                title: event.currentTarget.value,
              };
            }}
          />
        </label>
        <button
          class="icon-button"
          aria-label="Edit script text"
          disabled={busy.value}
          onClick={function () {
            const wordTexts = script.value.words.map(function (word) {
              return word.text;
            });
            if (isCards) {
              const cardTexts = cards.value.map(function (card) {
                const words = card.words.map(word => word.text);
                const text = words.join("");
                return text.trim();
              });
              editorDraft.value = cardTexts.join("\n\n");
            } else {
              editorDraft.value = wordTexts.join("");
            }
            editing.value = true;
          }}
        >
          <Icon name="edit" />
        </button>
      </div>
      <div class="script-meta">
        <span>
          {script.value.words.length} words <span class="dot">·</span>{" "}
          {isCards
            ? `${cards.value.length} cards · Tap to advance`
            : `${formatTime(duration.value)} reading time`}
        </span>
        <span class="saved">
          <Icon name="check" size={13} />{" "}
          {storageNotice.value ? "Unsaved changes" : "Saved on device"}
        </span>
      </div>
      <div class="prompt-mode">
        <label for="prompt-mode">Prompt mode</label>
        <select
          id="prompt-mode"
          value={script.value.mode || "teleprompter"}
          disabled={busy.value || editing.value}
          onChange={(event) =>
            changePromptMode(event.currentTarget.value as PromptMode)
          }
        >
          <option value="teleprompter">Teleprompter</option>
          <option value="cue-cards">Cue cards</option>
        </select>
        <p>
          {isCards
            ? "One idea at a time. Separate cards with a blank line, or import one JSON cue per card."
            : "A scrolling script paced to your reading speed."}
        </p>
      </div>
      <div class="script-transfer-actions">
        <button
          class="outline-button"
          disabled={busy.value || editing.value}
          onClick={function () {
            setImportOpen(true);
          }}
        >
          Import JSON
        </button>
      </div>
      {importOpen && (
        <ScriptTransfer
          onClose={function () {
            setImportOpen(false);
          }}
        />
      )}
      {editing.value ? (
        <form
          class="text-editor"
          onSubmit={function (event) {
            event.preventDefault();
            replaceScript({
              ...script.value,
              words: isCards
                ? parseCards(editorDraft.value)
                : parseWords(editorDraft.value),
            });
            editing.value = false;
          }}
        >
          <label for="script-text">
            Edit your script{" "}
            <small>
              {isCards
                ? "Separate cards with a blank line. Changing text resets directions."
                : "Changing the text resets word directions."}
            </small>
          </label>
          <textarea
            id="script-text"
            value={editorDraft.value}
            maxLength={30000}
            onInput={function (event) {
              editorDraft.value = event.currentTarget.value;
            }}
          />
          <div class="row">
            <button
              type="button"
              class="quiet"
              onClick={function () {
                editing.value = false;
              }}
            >
              Cancel
            </button>
            <button class="primary" type="submit">
              Apply script
            </button>
          </div>
        </form>
      ) : (
        <>
          {!isCards && (
            <div class="selection-tools">
              <span>Select</span>
              <div class="segmented" aria-label="Selection size">
                {[
                  ["word", "Word"],
                  ["sentence", "Sentence"],
                  ["range", "Range"],
                  ["all", "All"],
                ].map(function ([value, label]) {
                  return (
                    <button
                      disabled={busy.value}
                      aria-pressed={scope.value === value}
                      onClick={function () {
                        changeScope(value);
                      }}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <div class="script-words" aria-label="Select words to direct">
            {script.value.words.length === 0 ? (
              <p class="empty-script">Add text using Edit or Import JSON.</p>
            ) : (
              script.value.words.map(function (word, index) {
                const isSelected =
                  !!selected && index >= selected[0] && index <= selected[1];
                return (
                  <span
                    class={`word-wrap ${isCards && word.cueStart ? "card-start" : ""}`}
                    key={index}
                  >
                    <button
                      class={`word intensity-${word.intensity} ${isSelected ? "selected" : ""}`}
                      aria-pressed={isSelected}
                      aria-label={`${word.text.trim()}, ${word.pace} words per minute, ${["soft", "natural", "strong"][word.intensity]}`}
                      disabled={busy.value}
                      onClick={function () {
                        selectWord(index);
                      }}
                    >
                      {word.text.trim()}
                    </button>
                    {/\n/.test(word.text) ? (
                      <span class="paragraph-break" />
                    ) : (
                      " "
                    )}
                  </span>
                );
              })
            )}
          </div>
          <div class="selection-hint">
            {isCards ? "Tap any word to select its whole card." : selectionHint}
          </div>
          <div class="direction">
            <div class="direction-heading">
              <span class="eyebrow">READING DIRECTION</span>
              <span>{selectionLabel}</span>
            </div>
            <fieldset disabled={!selected || busy.value}>
              <div class="control-label">
                <label for="pace">
                  Pace{isCards ? " (unused in Cue cards)" : ""}
                </label>
                <output for="pace">
                  {paces.size > 1 ? "Mixed" : pace} <small>wpm</small>
                </output>
              </div>
              <input
                id="pace"
                type="range"
                min="60"
                max="240"
                step="5"
                value={pace}
                onInput={function (event) {
                  annotate("pace", Number(event.currentTarget.value));
                }}
              />
              <div class="range-labels">
                <span>60 wpm</span>
                <span>240 wpm</span>
              </div>
              <div class="control-label">
                <span id="emphasis-label">Emphasis</span>
              </div>
              <div
                class="emphasis-options"
                role="group"
                aria-labelledby="emphasis-label"
              >
                {["Soft", "Natural", "Strong"].map(function (label, value) {
                  return (
                    <button
                      aria-pressed={
                        intensities.size === 1 &&
                        selectedWords[0]?.intensity === value
                      }
                      class={`emphasis emphasis-${value}`}
                      onClick={function () {
                        annotate("intensity", value);
                      }}
                    >
                      <span>Aa</span>
                      {label}
                    </button>
                  );
                })}
              </div>
              <div class="pause-control">
                <label for="pause-after">
                  Pause after selection{" "}
                  <small>
                    {isCards ? "guidance only · milliseconds" : "milliseconds"}
                  </small>
                </label>
                <input
                  id="pause-after"
                  type="number"
                  min="0"
                  max="10000"
                  step="100"
                  inputMode="numeric"
                  value={selectedWords.at(-1)?.pauseAfterMs || 0}
                  onInput={function (event) {
                    setSelectionPause(Number(event.currentTarget.value));
                  }}
                />
              </div>
            </fieldset>
          </div>
        </>
      )}
    </section>
  );
}
